import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// The Tasks grid's "Next year" cell: what became of this file the following
// year. Looking back at a finished year, a completed task used to read the
// same whether it had rolled forward, was still waiting to, or never would --
// T & D Holdings, 2026-10-20. The state comes from the server
// (TaskSerializer.next_year), which shares its "won't roll" rule with the
// roll-forward engine, so the cell can't call "waiting" a file a run would skip.
//
//   rolled  -- link to next year's task
//   waiting -- finished, nothing next year: roll it now, or say it won't roll
//   stopped -- won't roll, with the reason; undoable when staff stopped it
//              (an inactive client or a deceased taxpayer is fixed at source)
//
// onRoll(row) opens the usual roll-forward pop-up; onStop(row, patch) PATCHes
// roll_stop_reason / roll_stop_note and resolves once saved.

export const STOP_REASONS = [
  { value: "dissolved", label: "Dissolved" },
  { value: "client_left", label: "Client left" },
  { value: "amalgamated", label: "Amalgamated" },
  { value: "other", label: "Other" },
];

export default function NextYearCell({ row, onRoll, onStop }) {
  const [open, setOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [reason, setReason] = useState("dissolved");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const next = row.next_year;
  if (!next) return <span style={{ opacity: 0.4 }}>—</span>;

  if (next.state === "rolled") {
    return (
      <Link
        to={`/tasks/${next.task_id}`}
        title="Next year's task"
        style={{ ...pill, background: "#dcfce7", color: "#166534", textDecoration: "none" }}
      >
        ✓ FY {next.fiscal_year}
      </Link>
    );
  }

  const staffStopped = next.state === "stopped" && Boolean(row.roll_stop_reason);
  if (next.state === "stopped" && !staffStopped) {
    return (
      <span title={`Won't roll forward: ${next.reason}`}
            style={{ ...pill, background: "#f3f4f6", color: "#6b7280" }}>
        Won't roll
      </span>
    );
  }

  const save = async (patch) => {
    setBusy(true);
    setError("");
    try {
      await onStop(row, patch);
      setOpen(false);
      setStopping(false);
    } catch (e) {
      setError(e?.message || "Could not save that.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    setOpen((v) => !v);
    setStopping(false);
    setError("");
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      {staffStopped ? (
        <button type="button" onClick={toggle} title={next.reason}
                style={{ ...pill, ...btnReset, background: "#f3f4f6", color: "#6b7280" }}>
          Won't roll: {next.reason}
        </button>
      ) : (
        <button type="button" onClick={toggle}
                title="Finished, and nothing for next year yet"
                style={{ ...pill, ...btnReset, background: "#fef3c7", color: "#92400e" }}>
          Waiting
        </button>
      )}

      {open && (
        <div role="dialog" aria-label="Next year" style={menu}>
          {staffStopped ? (
            <button type="button" style={menuItem} disabled={busy}
                    onClick={() => save({ roll_stop_reason: "", roll_stop_note: "" })}>
              Roll forward after all
            </button>
          ) : !stopping ? (
            <>
              <button type="button" style={menuItem}
                      onClick={() => { setOpen(false); onRoll(row); }}>
                Roll forward now…
              </button>
              <button type="button" style={menuItem} onClick={() => setStopping(true)}>
                Won't roll forward…
              </button>
            </>
          ) : (
            <div style={{ display: "grid", gap: 6, padding: 4 }}>
              <label style={label}>
                Reason
                <select value={reason} onChange={(e) => setReason(e.target.value)}
                        style={field}>
                  {STOP_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
              <label style={label}>
                Note{reason === "other" ? "" : " (optional)"}
                <input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder={reason === "amalgamated" ? "e.g. with TIL, July 31" : ""}
                       style={field} />
              </label>
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button type="button" style={menuItem} onClick={() => setStopping(false)}>
                  Cancel
                </button>
                <button type="button" style={{ ...menuItem, fontWeight: 600 }}
                        disabled={busy || (reason === "other" && !note.trim())}
                        onClick={() => save({ roll_stop_reason: reason, roll_stop_note: note.trim() })}>
                  Save
                </button>
              </div>
            </div>
          )}
          {error && <div style={{ color: "#b91c1c", fontSize: 12, padding: 4 }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

const pill = {
  display: "inline-block", padding: "2px 8px", borderRadius: 10,
  fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", maxWidth: "100%",
  overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle",
};
const btnReset = { border: "none", cursor: "pointer", font: "inherit", fontSize: 11 };
const menu = {
  position: "absolute", top: "calc(100% + 4px)", left: "50%",
  transform: "translateX(-50%)", background: "white", border: "1px solid #d0d4e4",
  borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth: 200,
  zIndex: 300, padding: 4, textAlign: "left", color: "#323338",
};
const menuItem = {
  display: "block", width: "100%", textAlign: "left", padding: "6px 8px",
  background: "transparent", border: "none", cursor: "pointer", fontSize: 13,
  borderRadius: 4,
};
const label = { display: "grid", gap: 3, fontSize: 11, fontWeight: 600, color: "#6b7280" };
const field = {
  padding: "4px 6px", fontSize: 13, border: "1px solid #d0d4e4", borderRadius: 4,
};
